import { computed, ref, watch } from 'vue'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase'
import { apiPost } from '@/composables/useApi'
import {
  getAvailableTranscriptionLanguages,
  getDefaultTranscriptionLanguage,
  transcriptionProviders,
} from '@/utils/transcriptionOptions'

const TRANSCRIPTION_WAIT_TIMEOUT_MS = 5 * 60 * 1000

function waitForTranscription(audioId) {
  return new Promise((resolve, reject) => {
    const fileRef = doc(db, 'files', audioId)
    let unsubscribe = () => {}
    let settled = false

    const finish = (callback, value) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      unsubscribe()
      callback(value)
    }

    const timeoutId = window.setTimeout(() => {
      finish(reject, new Error('Subtitle generation is taking longer than expected. Please check the file again shortly.'))
    }, TRANSCRIPTION_WAIT_TIMEOUT_MS)

    unsubscribe = onSnapshot(
      fileRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          finish(reject, new Error('The audio file no longer exists.'))
          return
        }

        const fileData = snapshot.data() || {}
        const hasSubtitles = Array.isArray(fileData.subtitles) && fileData.subtitles.length > 0
        const isCompleted = (
          fileData.transcriptionUsageStatus === 'completed'
          || (fileData.processingStage === 'completed' && hasSubtitles)
        )
        const isFailed = (
          fileData.status === 'failed'
          || fileData.processingStage === 'failed'
          || fileData.transcriptionUsageStatus === 'refunded'
        )

        if (isCompleted) {
          finish(resolve, { id: audioId, ...fileData })
        } else if (isFailed) {
          finish(reject, new Error(fileData.processingError || 'Subtitle generation failed.'))
        }
      },
      (error) => finish(reject, error),
    )
  })
}

/**
 * 字幕生成 composable
 * 管理生成状态、确认对话框、API 调用
 */
export function useSubtitleGeneration() {
  const isGenerating = ref(false)
  const showConfirmDialog = ref(false)
  const requiredUsage = ref(10)
  const selectedTranscriptionProvider = ref('deepgram')
  const selectedTranscriptionLanguage = ref('en')
  const openGenerationDropdown = ref(null)
  const availableTranscriptionLanguages = computed(() => (
    getAvailableTranscriptionLanguages(selectedTranscriptionProvider.value)
  ))

  watch(selectedTranscriptionProvider, (provider) => {
    selectedTranscriptionLanguage.value = getDefaultTranscriptionLanguage(provider)
  })

  watch(showConfirmDialog, (isOpen) => {
    if (!isOpen) openGenerationDropdown.value = null
  })

  function toggleGenerationDropdown(name) {
    openGenerationDropdown.value = openGenerationDropdown.value === name ? null : name
  }

  function closeGenerationDropdown() {
    openGenerationDropdown.value = null
  }

  function selectGenerationProvider(provider) {
    selectedTranscriptionProvider.value = provider
    closeGenerationDropdown()
  }

  function selectGenerationLanguage(language) {
    selectedTranscriptionLanguage.value = language
    closeGenerationDropdown()
  }

  function generateSubtitles(audioDuration) {
    if (isGenerating.value) return
    requiredUsage.value = Math.ceil(audioDuration / 6)
    showConfirmDialog.value = true
  }

  /**
   * 确认生成字幕
   * @param {Object} params - 生成参数
   * @param {string} params.audioId
   * @param {string} params.originalText
   * @param {number} params.remainingUsage
   * @param {'deepgram'|'openai'} params.transcriptionProvider
   * @param {string} params.transcriptionLanguage
   * @returns {Promise<{success: boolean, fileData?: Object, error?: string}>}
   */
  async function confirmGenerate({
    audioId,
    originalText,
    remainingUsage,
    transcriptionProvider,
    transcriptionLanguage,
  }) {
    showConfirmDialog.value = false

    if (remainingUsage < requiredUsage.value) {
      return { success: false, insufficientUsage: true }
    }

    try {
      isGenerating.value = true
      const data = await apiPost('/api/transcribe', {
        fileId: audioId,
        text: originalText,
        transcriptionProvider,
        transcriptionLanguage,
      })

      if (data.accepted || data.status === 'processing') {
        const fileData = await waitForTranscription(audioId)
        return { success: true, fileData }
      }

      if (data.duration) {
        // 获取最新的文件数据
        const fileDoc = await getDoc(doc(db, 'files', audioId))
        if (fileDoc.exists()) {
          return { success: true, fileData: { id: audioId, ...fileDoc.data() } }
        }
        return { success: true }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: error.message, insufficientUsage: error.code === 'INSUFFICIENT_USAGE' }
    } finally {
      isGenerating.value = false
    }
  }

  return {
    isGenerating,
    showConfirmDialog,
    requiredUsage,
    selectedTranscriptionProvider,
    selectedTranscriptionLanguage,
    openGenerationDropdown,
    transcriptionProviders,
    availableTranscriptionLanguages,
    toggleGenerationDropdown,
    closeGenerationDropdown,
    selectGenerationProvider,
    selectGenerationLanguage,
    generateSubtitles,
    confirmGenerate
  }
}
